import { firstValueFrom } from 'rxjs'

import { Injectable } from '@angular/core'
import { MapSpace } from '@classes/MapSpace'
import { ModelSpace } from '@classes/ModelSpace'

import { AwsService } from './aws.service'
import { EnvironmentManagerService } from './environment-manager.service'
import { ModelService } from './model.service'
import { SceneService } from './scene.service'
import { CommandService } from './command.service'

@Injectable({
  providedIn: 'root'
})
export class ThumbnailService {
  private get _sceneManager() { return this._envManager.sceneManager }
  private get _modelSpace() { return this._envManager.modelSpace }
  private get _selectedScene() { return this._sceneService.selectedScene }
  private get _selectedModel() { return this._modelService.selectedModel }

  constructor(
    private _awsService: AwsService,
    private _modelService: ModelService,
    private _envManager: EnvironmentManagerService,
    private _sceneService: SceneService,
    private _commandService: CommandService
  ) { }

  updateSceneThumbnail() {
    const scene = this._selectedScene
    const oldThumbnail = scene.thumbnail

    return this.getThumbnail(this._modelSpace).then(async (cropped) => {
      // Show cropped preview immediately
      scene.thumbnail = cropped
      scene.updateThumbnail = false

      // Upload to S3 and store s3ref:{id}
      try {
        const file = await this._base64ToFile(cropped, 'scene-thumbnail.png')
        const s3Ref = await firstValueFrom(this._awsService.uploadAndRegister(file, 'upload-scene-thumbnail', 'thumbnail'))
        const s3RefValue = `s3ref:${s3Ref.id}`
        // Persist s3ref to DB, then restore the base64 preview in memory
        // so the UI doesn't try to render "s3ref:7" as an img src
        this._commandService.update.scene(scene, 'thumbnail', s3RefValue, oldThumbnail).subscribe({
          next: () => { scene.thumbnail = cropped },
          error: () => { scene.thumbnail = oldThumbnail }
        })
      } catch {
        // Revert to old thumbnail so base64 never gets persisted to DB
        console.error('Failed to upload scene thumbnail to S3')
        scene.thumbnail = oldThumbnail
      }
    })
  }

  updateModelThumbnail(updateLocally = true) {
    const model = this._selectedModel
    const oldThumbnail = model.thumbnail

    return this.getThumbnail(this._modelSpace).then(async (cropped) => {
      // Show cropped preview immediately
      model.thumbnail = cropped

      // Upload to S3 and store s3ref:{id}
      try {
        const file = await this._base64ToFile(cropped, 'model-thumbnail.png')
        const s3Ref = await firstValueFrom(this._awsService.uploadAndRegister(file, 'upload-model-thumbnail', 'thumbnail'))
        const s3RefValue = `s3ref:${s3Ref.id}`
        // Send s3ref to the backend via a shallow copy — never assign the raw
        // s3ref: scheme to model.thumbnail because Angular change detection would
        // render it as an <img src="s3ref:N"> before we can restore the preview,
        // causing ERR_UNKNOWN_URL_SCHEME in the browser.
        const payload = { ...model, thumbnail: s3RefValue }
        await this._modelService.updateModel(payload as any, { updateLocally: false, toast: false }).toPromise()
        // If updateLocally was requested, update now with the base64 preview intact
        if (updateLocally) {
          this._modelService.editModelLocally(model, 'update')
        }
      } catch {
        console.error('Failed to upload model thumbnail to S3')
        model.thumbnail = oldThumbnail
      }
    })
  }

  private getThumbnail(space: ModelSpace | MapSpace) {
    const canvasHeight = space.canvas.height
    const canvasWidth = space.canvas.width

    space.renderer.render(this._sceneManager.renderedScene, space.camera)

    return this._getCanvasScreenshot(space)
      .then((screenShot: string) => this.cropScreenshot(screenShot, canvasWidth, canvasHeight))
  }

  private _getCanvasScreenshot(space: ModelSpace | MapSpace) {
    return new Promise<string>((resolve) => {
      if (space instanceof MapSpace) {
        space.map.once('render', () => resolve(space.canvas.toDataURL('image/png')))
      } else {
        resolve(space.canvas.toDataURL("image/png"))
      }
    })
  }

  cropScreenshot(screenShot: string, width: number, height: number) {
    const cropCanvas = document.createElement('canvas')
    const contex = cropCanvas.getContext("2d")
    const imageElement = document.createElement('img')
    const landscape: boolean = width > height

    imageElement.src = screenShot
    cropCanvas.width = 500
    cropCanvas.height = 500
    cropCanvas.append(imageElement)

    return new Promise<string>((resolve, reject) => {
      imageElement.onload = () => {
        const srcCanvasX = landscape ? (width - height) / 2 : 0
        const srcCanvasY = landscape ? 0 : (height - width) / 2
        const srcCanvasWidth = landscape ? height : width
        const srcCanvasHeight = landscape ? height : width
        const destCanvasX = 0
        const destCanvasY = 0
        const destCanvasWidth = 500
        const destCanvasHeight = 500

        contex.drawImage(imageElement,
          srcCanvasX, srcCanvasY,
          srcCanvasWidth, srcCanvasHeight,
          destCanvasX, destCanvasY,
          destCanvasWidth, destCanvasHeight)

        resolve(cropCanvas.toDataURL("image/png"))

        cropCanvas.remove()
        imageElement.remove()
      }
    })
  }

  /** Converts a base64 data URL to a File object */
  private async _base64ToFile(base64: string, filename: string): Promise<File> {
    const res = await fetch(base64)
    const blob = await res.blob()
    return new File([blob], filename, { type: blob.type || 'image/png' })
  }
}
